import { ChangeDetectionStrategy, Component, Input, ViewChild } from '@angular/core'
import { MatMenuTrigger } from '@angular/material/menu'
import { TranslateService } from '@ngx-translate/core'
import { filter, Observable, switchMap, takeUntil } from 'rxjs'

import { DestroyService } from '@core/services/destroy.service'
import { DialogService } from '@shared/dialogs/dialog.service'
import { ChatModel, ChatUserModel } from '@shared/models/messenger/messenger.model'
import { User } from '@shared/models/user.model'
import { ChatCreateFormComponent } from '../chat-create-form/chat-create-form.component'
import { MessengerApiService } from '../../services/messenger-api.service'
import { AvatarSize } from '@shared/common-components/avatar/avatar.model'
import { InputMessageDialogComponent } from '@shared/dialogs/input-message-dialog/input-message-dialog.component'
import { Router } from '@angular/router'
import { MessengerService } from '@app/modules/client/messenger/services/messenger.service'
import { UtilityService } from '@core/services/utility.service'
import { sameId } from '@core/helpers/same-id'

@Component({
  selector: 'lin-chat-details-user-list',
  templateUrl: './chat-details-user-list.component.html',
  styleUrls: ['./chat-details-user-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DestroyService],
  standalone: false,
})
export class ChatDetailsUserListComponent {
  AvatarSize = AvatarSize

  @Input() currentChat: ChatModel
  @Input() currentUser: User
  @Input() isGroupChat: boolean
  @Input() isMyChat: boolean
  @ViewChild(MatMenuTrigger) trigger: MatMenuTrigger

  get isAdmin(): boolean {
    return this.currentUser.role.name === 'admin'
  }

  get chatUsers(): ChatUserModel[] {
    return this.currentChat?.users?.filter(({ id }) => !sameId(id, this.currentUser.id))
  }

  get chatName(): string {
    return this.chatUsers.length ? this.chatUsers.map((user) => user.fullName).join(', ') : '-'
  }

  get avatarPersonal(): ChatUserModel {
    return this.currentChat?.users.filter(({ id }) => !sameId(id, this.currentUser.id))?.[0]
  }

  constructor(
    public readonly messengerService: MessengerService,
    private readonly dialogService: DialogService,
    private readonly router: Router,
    private readonly translateService: TranslateService,
    private readonly destroyService: DestroyService,
    private readonly messengerApiService: MessengerApiService,
    private readonly utilityService: UtilityService,
  ) {}

  handleAddToChat(): void {
    const headerTitle = <string>this.translateService.instant('CLIENT.MESSENGER.CHAT_CREATE.EDIT')

    this.dialogService
      .open(ChatCreateFormComponent, headerTitle, { chat: this.currentChat, scope: 'edit' })
      .afterClosed()
      .pipe(takeUntil(this.destroyService))
      .subscribe()
  }

  handleDeleteFromChat(userId: string): void {
    const userRemoveId = userId
    const confirmText = <string>this.translateService.instant('CLIENT.MESSENGER.CHAT_DETAILS.EXIT_FROM_CHAT')

    const changeUserChat = (): Observable<ChatModel> => {
      const isRemovedId = (id: string): boolean => id !== userRemoveId
      const userIds = this.userIds().filter(isRemovedId)

      return this.messengerApiService.addRemoveUserToChat(this.currentChat.id, { userIds })
    }

    this.dialogService
      .confirm(confirmText)
      .afterClosed()
      .pipe(filter(Boolean), switchMap(changeUserChat), takeUntil(this.destroyService))
      .subscribe()
  }

  trackBy(_, { id }: ChatUserModel): string {
    return id
  }

  handleLeaveChat(): void {
    this.handleDeleteFromChat(this.currentUser.id)
  }

  handleBackToList(): void {
    void this.router.navigate(['client', 'messenger'])
  }

  handleJoinChat(): void {
    const userIds = this.userIds()
    userIds.push(this.currentUser.id)

    this.messengerApiService.addRemoveUserToChat(this.currentChat.id, { userIds }).pipe(takeUntil(this.destroyService)).subscribe()
  }

  private userIds(): string[] {
    const toIds = ({ id }: ChatUserModel): string => id

    return this.currentChat.users.map(toIds)
  }

  getNetworkStatusTitle(user: ChatUserModel): string {
    const lastLogin = user?.lastLoggedAt
      ? this.utilityService.dayjsDateTimeFormatting(user.lastLoggedAt)
      : <string>this.translateService.instant('CLIENT.MESSENGER.CHAT_DETAILS.NEVER')

    return user?.networkStatus
      ? <string>this.translateService.instant('CLIENT.MESSENGER.CHAT_DETAILS.ONLINE')
      : <string>this.translateService.instant('CLIENT.MESSENGER.CHAT_DETAILS.LAST_LOGGED_AT', { value: lastLogin })
  }
}
